import React from 'react';
/**
 * THE VIGNETTE — how a product screen is presented, not what it contains.
 *
 * A UI panel dropped on a page terminates in four hard corners and a shadow,
 * which frames it as an ARTEFACT: a screenshot someone pasted in. The treatment
 * this borrows does the opposite — the artwork runs wider than the reading
 * column and dissolves into the ground at its edges, so it reads as a window
 * onto something larger that happens to be lit where you are looking. That is
 * also what lets the artwork be BIG: a panel that must fit inside the text
 * column has to shrink until its type is illegible.
 *
 * TWO THINGS THAT MADE THE FIRST VERSION LOOK BROKEN, both fixed here:
 *
 * 1. THE SCRIM MUST OUT-REACH THE ARTWORK. The children bleed sideways past
 *    the wrapper, so a scrim sized to the wrapper stopped short and left the
 *    bled edges at full brightness with a visible vertical seam where the
 *    darkening began. The scrim now extends by MORE than the bleed, in the
 *    same units, so there is no edge for it to stop at.
 *
 * 2. ONE LAYER, NOT TWO. A radial and a separate bottom linear gradient with
 *    different horizontal extents crossed each other, and the join read as a
 *    faint rectangle sitting over the panel. The downward fall is now part of
 *    the same radial (its focus sits high, so the gradient naturally runs
 *    longer below than above).
 *
 * The scrim is painted in the SECTION'S OWN GROUND COLOUR rather than applied
 * as a CSS mask: a mask would also eat pointer targets and clip the table's
 * hover panel, whereas a `pointer-events-none` overlay is visually identical
 * and leaves the artwork fully interactive underneath.
 */
export function ProductVignette({
  children,
  ground = '#1A120C',
  bleed = true,
  focusY = 34,
  bleedClass,
  scrimClass,
  className = ''
}: {
  children: React.ReactNode;
  /** The section's background colour. The fade must land on this exactly, or
   *  the edges read as grey haze sitting on top of the page. */
  ground?: string;
  /** Let the artwork run past the reading column on wide screens. */
  bleed?: boolean;
  /** Where the light sits, as a % down the frame. Default is high, over the
   *  panel's header. */
  focusY?: number;
  /** Override the bleed when the artwork sits in a column with content beside
   *  it and can only run past ONE edge. */
  bleedClass?: string;
  /** Override the scrim's reach. Must always out-reach whatever bleed is in
   *  use, or the fade stops short and leaves a seam. */
  scrimClass?: string;
  className?: string;
}) {
  return <div className={`relative ${className}`} data-id="element-1057" data-mp="2sdxu1d5td0a2">
      <div className={bleedClass ?? (bleed ? 'lg:-mx-[5%] xl:-mx-[7%]' : '')} data-id="element-1058" data-mp="2sd5whwsnosh5">{children}</div>

      {/* Deliberately larger than the bled artwork at every breakpoint, so the
       gradient never has a hard edge inside the frame. */}
      <div aria-hidden="true" className={`pointer-events-none absolute ${scrimClass ?? '-inset-x-[10%] -top-16 bottom-[-14%] lg:-inset-x-[16%]'}`} style={{
      background: `radial-gradient(78% 62% at 50% ${focusY}%, transparent 0%, transparent 46%, ${ground}A6 76%, ${ground} 96%)`
    }} data-id="element-1059" data-mp="1h0vdgi3psapw" />
    </div>;
}